// react
import { useCallback } from 'react';

// hooks
import { SSENotification, useSSE } from './useSSE';

interface UseV2StatusListenerProps {
	/** The v2 resource this page is showing (matches sseNotify.ts on the server). */
	resource: 'contract' | 'invoice' | 'voucher' | 'spendplan' | 'workplan' | 'condition';
	/** The entity id this page is showing. When undefined / falsy the hook is a no-op. */
	id: number | null | undefined;
	/** Called when a matching v2:<resource>:statusChanged event arrives for this id. */
	onChange: () => void;
}

/**
 * Subscribes to bldt-notifications SSE and invokes `onChange` whenever a
 * v2:<resource>:statusChanged event arrives whose payload.id matches the
 * page's current entity. Used by detail pages (PageInvoice, PageContract,
 * PageVoucher, PageWorkPlan, PageSpendPlan) so the header status chip and any
 * other locally-fetched fields refetch as soon as someone else approves /
 * rejects / progresses the record.
 *
 * The list-level refetch (so navigation back to the list shows fresh data) is
 * handled separately by SpendPlanProvider in ProjectV2.context.ts.
 */
export const useV2StatusListener = ({ resource, id, onChange }: UseV2StatusListenerProps) => {
	const filter = useCallback(
		(n: SSENotification) => {
			if (!id) return false;
			if (typeof n.type !== 'string' || n.type !== `v2:${resource}:statusChanged`) {
				return false;
			}
			const data = n.payload?.message;
			if (!data || typeof data !== 'object') return false;
			return Number((data as Record<string, unknown>).id) === id;
		},
		[id, resource]
	);

	const onNotification = useCallback(() => {
		onChange();
	}, [onChange]);

	useSSE({ filter, onNotification });
};
